/*!
 * egt.select.datetimespan — "best time to contact you" span picker (app-scoped).
 *
 * The spec (§1) asks for a date-time SPAN selection. ua.select.date is
 * date-only with no time and no range, so this composes a small, robust
 * picker: an optional preferred date + start/end time-of-day inputs, plus
 * day-part "window" chips (Morning / Afternoon / Evening) and weekday-vs-
 * weekend preference. None of it is required — it expresses a preference.
 *
 * Renders raw DOM inputs (NOT nested <uae>) so it is one uae[name] boundary.
 *
 * save() -> { date, startTime, endTime, windows:[], days } (omitting empties)
 * load() -> restores the same shape.
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.select.datetimespan",
  title: "Date/Time Span",
  description: "Pick a preferred contact date, a time-of-day span, and day-part windows.",
  control: true,
  preview: "egt.preview.dtspan",
  supportServer: false,
  fields: {
    name: {
      title: "Name",
      type: "string",
      description: "Form field name.",
      required: true
    },
    title: {
      title: "Title",
      type: "string",
      description: "Control title."
    },
    description: {
      title: "Description",
      type: "inner",
      description: "Helper text."
    },
    required: {
      title: "Required",
      type: "boolean",
      description: "Whether the user must pick at least one window or a time.",
      default: false
    },
    value: {
      title: "Value",
      type: "any",
      description: "Initial value object { date, startTime, endTime, windows, days }."
    }
  }
});

var WINDOWS = [
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" }
];

var DAYS = [
  { value: "weekdays", label: "Weekdays" },
  { value: "weekends", label: "Weekends" },
  { value: "any", label: "Any day" }
];

function normalizeValue(value) {
  var v = (value && typeof value === "object") ? value : {};
  return {
    date: (v.date || "").toString(),
    startTime: (v.startTime || "").toString(),
    endTime: (v.endTime || "").toString(),
    windows: v.windows instanceof Array ? v.windows.map(function (w) { return (w || "").toString(); }).filter(Boolean) : [],
    days: (v.days || "").toString()
  };
}

exports.render = async function (options) {
  var name = (options.name || "").toString().trim();
  var title = (options.title || "").toString().trim();
  var description = (options.description || "").toString().trim();
  var required = options.required === true;
  var initial = normalizeValue(options.value);

  var wrapper = document.createElement("div");
  wrapper.className = "form-group pb-3 egt-dtspan";
  wrapper.setAttribute("name", name);
  wrapper.setAttribute("data-required", required ? "true" : "false");

  if (title) {
    var label = document.createElement("label");
    label.className = "form-label fw-semibold";
    label.textContent = title;
    wrapper.appendChild(label);
  }

  if (description) {
    var help = document.createElement("div");
    help.className = "form-text mb-2";
    help.textContent = description;
    wrapper.appendChild(help);
  }

  // Day-part window chips (multi-select)
  var windowsGroup = document.createElement("div");
  windowsGroup.className = "d-flex flex-wrap gap-2 mb-2 egt-dtspan__windows";
  for (var i = 0; i < WINDOWS.length; i++) {
    var chip = document.createElement("button");
    chip.type = "button";
    chip.className = "btn btn-sm btn-outline-secondary egt-dtspan__chip";
    chip.setAttribute("data-dtspan-window", WINDOWS[i].value);
    chip.setAttribute("aria-pressed", initial.windows.indexOf(WINDOWS[i].value) >= 0 ? "true" : "false");
    if (initial.windows.indexOf(WINDOWS[i].value) >= 0) chip.classList.add("active");
    chip.textContent = WINDOWS[i].label;
    windowsGroup.appendChild(chip);
  }
  wrapper.appendChild(windowsGroup);

  // Day preference chips (single-select)
  var daysGroup = document.createElement("div");
  daysGroup.className = "d-flex flex-wrap gap-2 mb-3 egt-dtspan__days";
  for (var d = 0; d < DAYS.length; d++) {
    var dchip = document.createElement("button");
    dchip.type = "button";
    dchip.className = "btn btn-sm btn-outline-secondary egt-dtspan__chip";
    dchip.setAttribute("data-dtspan-day", DAYS[d].value);
    dchip.setAttribute("aria-pressed", initial.days === DAYS[d].value ? "true" : "false");
    if (initial.days === DAYS[d].value) dchip.classList.add("active");
    dchip.textContent = DAYS[d].label;
    daysGroup.appendChild(dchip);
  }
  wrapper.appendChild(daysGroup);

  // Optional specific date + time span
  var grid = document.createElement("div");
  grid.className = "row g-2 egt-dtspan__grid";

  function field(colClass, labelText, inputType, attrName, val) {
    var col = document.createElement("div");
    col.className = colClass;
    var l = document.createElement("label");
    l.className = "form-label small mb-1";
    l.textContent = labelText;
    var input = document.createElement("input");
    input.type = inputType;
    input.className = "form-control form-control-sm";
    input.setAttribute(attrName, "true");
    if (val) input.value = val;
    col.appendChild(l);
    col.appendChild(input);
    return col;
  }

  grid.appendChild(field("col-12 col-lg-4", "Preferred date (optional)", "date", "data-dtspan-date", initial.date));
  grid.appendChild(field("col-6 col-lg-4", "From", "time", "data-dtspan-start", initial.startTime));
  grid.appendChild(field("col-6 col-lg-4", "To", "time", "data-dtspan-end", initial.endTime));
  wrapper.appendChild(grid);

  var invalid = document.createElement("div");
  invalid.className = "invalid-feedback d-block egt-dtspan__invalid";
  invalid.textContent = "";
  wrapper.appendChild(invalid);

  windowsGroup.addEventListener("click", function (event) {
    var chip = event.target.closest("[data-dtspan-window]");
    if (!chip) return;
    var on = chip.getAttribute("aria-pressed") === "true";
    chip.setAttribute("aria-pressed", on ? "false" : "true");
    chip.classList.toggle("active", !on);
  });

  daysGroup.addEventListener("click", function (event) {
    var chip = event.target.closest("[data-dtspan-day]");
    if (!chip) return;
    var on = chip.getAttribute("aria-pressed") === "true";
    var all = daysGroup.querySelectorAll("[data-dtspan-day]");
    for (var k = 0; k < all.length; k++) {
      all[k].setAttribute("aria-pressed", "false");
      all[k].classList.remove("active");
    }
    if (!on) {
      chip.setAttribute("aria-pressed", "true");
      chip.classList.add("active");
    }
  });

  return wrapper;
};

function collect(control) {
  var windows = [];
  var wchips = control.querySelectorAll("[data-dtspan-window][aria-pressed='true']");
  for (var i = 0; i < wchips.length; i++) {
    windows.push(wchips[i].getAttribute("data-dtspan-window"));
  }
  var dayChip = control.querySelector("[data-dtspan-day][aria-pressed='true']");
  var dateInput = control.querySelector("[data-dtspan-date]");
  var startInput = control.querySelector("[data-dtspan-start]");
  var endInput = control.querySelector("[data-dtspan-end]");
  return {
    date: dateInput ? (dateInput.value || "") : "",
    startTime: startInput ? (startInput.value || "") : "",
    endTime: endInput ? (endInput.value || "") : "",
    windows: windows,
    days: dayChip ? dayChip.getAttribute("data-dtspan-day") : ""
  };
}

async function save(name, control, repo) {
  var required = control.getAttribute("data-required") === "true";
  var data = collect(control);
  var hasAny = data.windows.length > 0 || data.date || data.startTime || data.endTime || data.days;

  var invalid = control.querySelector(".egt-dtspan__invalid");

  if (required && !hasAny) {
    repo.success = false;
    repo.errors = [{ type: "validation", message: "Please choose at least one time window or a date." }];
    if (invalid) invalid.textContent = "Please choose at least one time window or a date.";
    control.classList.add("is-invalid");
    repo.data = {};
    return repo;
  }

  if (invalid) invalid.textContent = "";
  control.classList.remove("is-invalid");

  // Strip empties for a clean stored object.
  var out = {};
  if (data.date) out.date = data.date;
  if (data.startTime) out.startTime = data.startTime;
  if (data.endTime) out.endTime = data.endTime;
  if (data.windows.length > 0) out.windows = data.windows;
  if (data.days) out.days = data.days;

  repo.success = true;
  repo.data = out;
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var v = normalizeValue(data);
  var dateInput = control.querySelector("[data-dtspan-date]");
  var startInput = control.querySelector("[data-dtspan-start]");
  var endInput = control.querySelector("[data-dtspan-end]");
  if (dateInput) dateInput.value = v.date;
  if (startInput) startInput.value = v.startTime;
  if (endInput) endInput.value = v.endTime;

  var wchips = control.querySelectorAll("[data-dtspan-window]");
  for (var i = 0; i < wchips.length; i++) {
    var on = v.windows.indexOf(wchips[i].getAttribute("data-dtspan-window")) >= 0;
    wchips[i].setAttribute("aria-pressed", on ? "true" : "false");
    wchips[i].classList.toggle("active", on);
  }
  var dchips = control.querySelectorAll("[data-dtspan-day]");
  for (var j = 0; j < dchips.length; j++) {
    var don = v.days === dchips[j].getAttribute("data-dtspan-day");
    dchips[j].setAttribute("aria-pressed", don ? "true" : "false");
    dchips[j].classList.toggle("active", don);
  }
}
module.exports.load = load;
